import { useState } from 'react';
import { SwiperSlide } from 'swiper/react';
import { Swiper } from 'swiper/types';

import { IconButton } from '@/components/elements';
import { useIsSmallerThan } from '@/components/utils';
import ArrowThinLeft from '@/icons/small/Arrow-Thin-Left.svg';
import ArrowThinRight from '@/icons/small/Arrow-Thin-Right.svg';

import { type ITestimonialProps } from '../Testimonial';
import { Testimonial } from '../Testimonial';
import {
  StyledIntro,
  StyledNavigationButton,
  StyledOutOfCopy,
  StyledSlider,
  StyledSwiper,
  StyledTestimonials,
  StyledTestimonialsWrapper,
  StyledWrapper,
} from './Testimonials.styled';

interface ITestimonialsProps {
  overline: string;
  headline: string;
  outOfLabel: string;
  showOriginalLabel: string;
  testimonials: Array<ITestimonialProps & { id: string }>;
}

interface ISwiperButtonProps {
  direction: 'prev' | 'next';
  label?: string;
  swiperIndex: number;
  swiperInstance?: Swiper;
}

const SwiperButton = ({
  direction,
  label,
  swiperInstance,
}: ISwiperButtonProps) => {
  const Buttons = {
    prev: (
      <IconButton
        variant="primaryAlt"
        label={label}
        icon={<ArrowThinLeft />}
        disabled={swiperInstance?.isBeginning}
        onClick={() => swiperInstance?.slidePrev()}
      />
    ),
    next: (
      <IconButton
        variant="primaryAlt"
        label={label}
        icon={<ArrowThinRight />}
        disabled={swiperInstance?.isEnd}
        onClick={() => swiperInstance?.slideNext()}
      />
    ),
  } as const;

  return Buttons[direction];
};

export const Testimonials = ({
  overline,
  headline,
  outOfLabel = '',
  showOriginalLabel = '',
  testimonials = [],
}: ITestimonialsProps) => {
  const [swiperIndex, setSwiperIndex] = useState<number>(0);
  const [swiperInstance, setSwiperInstance] = useState<Swiper>();

  const isTablet = useIsSmallerThan('LAPTOP');

  return (
    <StyledTestimonials>
      <StyledWrapper
        forwardedAs="div"
        width={{ laptop: 'l', tabletLandscape: 'm' }}
      >
        <StyledIntro
          headline={{
            label: headline,
            as: 'h2',
            size: 'h3',
          }}
          subtitle={overline}
          nested
        />

        {isTablet ? (
          <StyledSlider>
            <StyledSwiper
              spaceBetween={0}
              slidesPerView={1}
              onSlideChange={(swiper) => {
                setSwiperIndex(swiper.activeIndex);
              }}
              tag="div"
              wrapperTag="ul"
              initialSlide={0}
              centeredSlides={true}
              centeredSlidesBounds={true}
              keyboard={true}
              id="testimonials-swiper"
              onSwiper={(swiper) => setSwiperInstance(swiper)}
            >
              {testimonials.map(({ id, ...props }, index) => {
                return (
                  <SwiperSlide key={id} tag="li">
                    <Testimonial {...props} index={index} />
                  </SwiperSlide>
                );
              })}
            </StyledSwiper>
            <StyledNavigationButton>
              <SwiperButton
                label="prev"
                direction="prev"
                swiperIndex={swiperIndex}
                swiperInstance={swiperInstance}
              />
              <StyledOutOfCopy
                text={`${swiperIndex + 1} ${outOfLabel} ${testimonials.length}`}
                size="s"
              />
              <SwiperButton
                label="next"
                direction="next"
                swiperIndex={swiperIndex}
                swiperInstance={swiperInstance}
              />
            </StyledNavigationButton>
          </StyledSlider>
        ) : (
          <StyledTestimonialsWrapper>
            {testimonials.map(
              (
                {
                  id,
                  name,
                  location,
                  rating,
                  description,
                  image,
                  showOriginalLink,
                },
                index
              ) => (
                <Testimonial
                  key={id}
                  name={name}
                  location={location}
                  rating={rating}
                  description={description}
                  image={image}
                  showOriginalLabel={showOriginalLabel}
                  showOriginalLink={showOriginalLink}
                  index={index}
                />
              )
            )}
          </StyledTestimonialsWrapper>
        )}
      </StyledWrapper>
    </StyledTestimonials>
  );
};
